import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { supabase } from '../supabase/client';
import { appendMatches, replaceMatches, setExhausted, clearRecentlyActedIds, setUserIdsToLoad, setLoading } from '../Redux/store';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * MatchLoader - Loads matches based on user settings and location
 * Infinite scroll: loads more when ≤3 matches remain
 */
export default function MatchLoader() {
  const dispatch = useDispatch();
  const authUserId = useSelector((s) => s.user.authUserId);
  const profile = useSelector((s) => s.user.profile);
  const session = useSelector((s) => s.user.session);
  
  const matchList = useSelector((s) => s.matches.list);
  const refreshCounter = useSelector((s) => s.matches.refreshCounter);
  const recentlyActedIds = useSelector((s) => s.matches.recentlyActedIds);
  const exhausted = useSelector((s) => s.matches.exhausted);

  // Load new matches
  useEffect(() => {
    const firstTimeViews = profile?.first_time_views || {};
    if (!authUserId || !profile || !profile.id || !firstTimeViews.profile_welcome) {
      return;
    }

    const loadMatches = async (replace = false) => {
      try {
        dispatch(setLoading(true));

        const token = session?.access_token;
        if (!token) {
          dispatch(setLoading(false));
          return;
        }

        // Combine current match IDs with recently acted IDs for exclusion
        const currentMatchIds = matchList.map(m => m.id);
        const excludeIds = [...currentMatchIds, ...recentlyActedIds];

        const response = await fetch(`${API_URL}/get-matches`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ exclude_ids: excludeIds }),
        });

        if (!response.ok) {
          dispatch(setLoading(false));
          return;
        }

        const { matches } = await response.json();

        if (replace) {
          dispatch(replaceMatches(matches || []));
        } else {
          dispatch(appendMatches(matches || []));
        }

        // Set exhausted if no matches returned
        if (!matches || matches.length === 0) {
          dispatch(setExhausted(true));
        }

        // Load reviews for new match IDs
        if (matches && matches.length > 0) {
          const matchIds = matches.map(m => m.id);
          dispatch(setUserIdsToLoad(matchIds));
        }

        dispatch(clearRecentlyActedIds());
        dispatch(setLoading(false));
      } catch (error) {
        dispatch(setLoading(false));
      }
    };

    // Initial load or settings change (replace mode)
    if (matchList.length === 0 && !exhausted && firstTimeViews.profile_welcome) {
      loadMatches(true);
    }
    // Load more when ≤3 matches left (append mode)
    else if (matchList.length <= 3 && !exhausted && firstTimeViews.profile_welcome) {
      loadMatches(false);
    }
  }, [authUserId, profile, matchList.length, refreshCounter, exhausted, dispatch, session, recentlyActedIds]);

  return null;
}
